import {
  createRootRouteWithContext,
  Outlet,
  useMatches,
  useParams,
} from '@tanstack/react-router'
import { GeckoAnalyticsProvider } from 'features/analytics'
import { GeckoAuthProvider } from 'features/auth'
import { DataLayerProvider } from 'features/dataLayers'
import { DSAPIProvider } from 'features/dsapi'
import { DuckDbInstanceProvider } from 'features/duckDB'
import { useHoneycombSdk } from 'features/honeycomb'
import { GeckoLocaleProvider } from 'features/locale'
import { PageMaxWidthContext } from 'features/maxPageWidth'
import { Navbar } from 'features/navbar'
import { useTrackRecentRoutes } from 'features/navigation'
import { GeckoPermissionsProvider } from 'features/permissions'
import { RightDrawerProvider } from 'features/rightDrawer'
import { RolodexProvider, useRolodexEntries } from 'features/rolodex'
import { TermsModalProvider } from 'features/terms'
import isNil from 'lodash/isNil'
import React, { ReactNode, useMemo, useState } from 'react'
import styled from 'styled-components'
import { GeckoThemeProvider } from 'styles/GeckoTheme'
import type { Cache } from 'swr'

export const Route = createRootRouteWithContext<
  { cache: Cache } & CantileverPageProps
>()({
  component: RootComponent,
  errorComponent: () => <div>There was an error</div>,
})

function RootComponent() {
  const lastMatch = useMatches({ select: (matches) => matches.at(-1) })

  const cantileverPageProps: CantileverPageProps = React.useMemo(() => {
    const layoutFromStatic = (
      lastMatch?.staticData as
        | { layout?: Partial<CantileverPageProps> }
        | undefined
    )?.layout
    const contextFlags =
      (lastMatch?.context as Partial<CantileverPageProps>) ?? {}

    const defaults: CantileverPageProps = {
      enableScrolling: false,
      enforceMaxWidth: false,
      showNavbar: true,
      showSearchBar: true,
      defaultClosedNav: false,
    }
    return { ...defaults, ...contextFlags, ...(layoutFromStatic ?? {}) }
  }, [lastMatch])

  return (
    <GeckoThemeProvider>
      <GeckoAuthProvider>
        <GeckoPermissionsProvider>
          <GeckoLocaleProvider>
            <GeckoAnalyticsProvider>
              <DSAPIProvider>
                <RolodexProvider>
                  <DataLayerProvider>
                    <DuckDbInstanceProvider>
                      <TermsModalProvider>
                        <RightDrawerProvider>
                          <CantileverPage {...cantileverPageProps}>
                            <Outlet />
                          </CantileverPage>
                        </RightDrawerProvider>
                      </TermsModalProvider>
                    </DuckDbInstanceProvider>
                  </DataLayerProvider>
                </RolodexProvider>
              </DSAPIProvider>
            </GeckoAnalyticsProvider>
          </GeckoLocaleProvider>
        </GeckoPermissionsProvider>
      </GeckoAuthProvider>
    </GeckoThemeProvider>
  )
}

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  flex: 1 1 auto;
  height: 100vh; /* Ensure the layout takes full viewport height */
  overflow: hidden; /* Prevent overflow of content beyond the viewport */
`

const PageBg = styled.div<{ $enableScrolling: boolean }>`
  overflow-y: ${({ $enableScrolling }) =>
    $enableScrolling ? 'auto' : 'hidden'}; /* Enable scrolling */
  background-color: ${({ theme }) => theme.forecast.pageBackground1};
  position: relative;
  width: 100%;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
`

const ContentContainer = styled.div<{
  $maxWidth?: string
  $enforceMaxWidth?: boolean
}>`
  display: flex;
  flex-direction: column;
  height: 100%;
  margin: ${({ $enforceMaxWidth }) => ($enforceMaxWidth ? '0 auto' : '0')};
  max-width: ${({ $maxWidth }) => $maxWidth};
  width: 100%;
`

type CantileverPageProps = {
  enableScrolling?: boolean
  enforceMaxWidth?: boolean
  showNavbar?: boolean
  showSearchBar?: boolean
  defaultClosedNav?: boolean
}

const CantileverPage: React.FC<
  CantileverPageProps & { children: ReactNode }
> = ({
  enableScrolling = false,
  enforceMaxWidth = false,
  showNavbar = true,
  showSearchBar = true,
  defaultClosedNav = false,
  children,
}) => {
  const orgOrSiteID = useRouteOrgSiteID()
  const [maxWidth, setMaxWidth] = useState<string>('64rem')

  useTrackRecentRoutes()

  useHoneycombSdk()

  return (
    <AppContainer>
      {showNavbar && (
        <Navbar
          orgOrSiteID={orgOrSiteID}
          defaultClosed={defaultClosedNav}
          hideSearchBar={!showSearchBar}
        />
      )}
      <PageMaxWidthContext.Provider value={{ maxWidth, setMaxWidth }}>
        <PageBg $enableScrolling={enableScrolling}>
          <ContentContainer
            $maxWidth={enforceMaxWidth ? maxWidth : 'none'}
            $enforceMaxWidth={enforceMaxWidth}>
            {children}
          </ContentContainer>
        </PageBg>
      </PageMaxWidthContext.Provider>
    </AppContainer>
  )
}

/**
 * Infers the site or org ID from route params.
 * First tries explicit params (orgOrSiteID, siteID, orgID).
 * If none found, looks up all param values in rolodex to infer
 * site/org from lower-level objects (e.g. for details pages to still render site context).
 */
function useRouteOrgSiteID(): string | undefined {
  const params = useParams({ strict: false })

  const knownOrgOrSite = params.orgOrSiteID ?? params.siteID ?? params.orgID

  const rolodexFetchProps = useMemo(
    () =>
      Object.entries(params)
        .filter(([, value]) => !isNil(value))
        .map(([, value]) => ({ ids: [value] })),
    [params]
  )

  const { entries } = useRolodexEntries(
    isNil(knownOrgOrSite) ? rolodexFetchProps : undefined
  )

  if (!isNil(knownOrgOrSite)) {
    return knownOrgOrSite
  }

  const siteFromTags = entries
    .map((entry) => entry.tags.site)
    .find((site) => !isNil(site))

  if (!isNil(siteFromTags)) {
    return siteFromTags
  }

  const orgFromTags = entries
    .map((entry) => entry.tags.organization)
    .find((org) => !isNil(org))

  return orgFromTags
}
